import { memo, useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Tag, Button, Tooltip, Popover, Space, message } from 'antd';
import { CrownOutlined, LoadingOutlined, DownOutlined, EditOutlined, DeleteOutlined, CopyOutlined, UpOutlined } from '@ant-design/icons';
import { PiChartBar } from 'react-icons/pi';
import GeometryPreview from './GeometryPreview';
import { formatDate } from '~/utils/dateUtils';

interface MobileCardItem {
  id: string;
  title: string;
  description?: string;
  creator?: string;
  creatorId?: string;
  date?: string;
  createdAt?: string; // Ngày tạo
  syncAt?: string;    // Ngày đồng bộ
  formName?: string;  // Tên biểu mẫu
  lat?: number;       // Latitude
  lng?: number;       // Longitude
  geom?: string;      // Raw geometry data for preview
  // Additional fields for members
  username?: string;
  email?: string;
  phoneNumber?: string;
  roles?: string;
  rolesArray?: any[]; // Array of role objects for Tag rendering
  isOwner?: boolean;
  tier?: string;
  expireDate?: string;
  [key: string]: any; // For additional properties
}

interface MobileCardListProps {
  data: MobileCardItem[];
  loading?: boolean;
  selectedId?: string;
  onItemClick?: (item: MobileCardItem) => void;
  onCreatorClick?: (creatorId: string) => void;
  onEditClick?: (item: MobileCardItem) => void;
  onDeleteClick?: (item: MobileCardItem) => void;
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
    onChange: (page: number, pageSize: number) => void;
  };
  // New props for load more functionality
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  // Form data for dynamic fields
  formData?: {
    content?: Array<{
      key: string;
      label?: string;
      type?: string;
    }>;
  };
}

const MobileCardList = ({
  data,
  loading,
  selectedId,
  onItemClick,
  onCreatorClick,
  onEditClick,
  onDeleteClick,
  pagination,
  hasMore,
  onLoadMore,
  loadingMore,
  formData
}: MobileCardListProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState<string | null>(null);
  const [expandedMetadata, setExpandedMetadata] = useState<Set<string>>(new Set());

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('Đã copy tọa độ vào clipboard');
    }).catch(() => {
      message.error('Không thể copy tọa độ');
    });
  };

  const toggleMetadata = (itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedMetadata(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const getRoleDisplayName = (role: string) => {
    const roleMap: { [key: string]: string } = {
      PROJECT_COLLECTOR: 'Thu thập',
      PROJECT_ADMIN: 'Quản trị',
      PROJECT_VIEWER: 'Xem',
      PROJECT_OWNER: 'Chủ dự án',
    };
    return roleMap[role] || role;
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingCard />
        <LoadingCard />
        <LoadingCard />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <CardList>
        {data.map((item) => (
          <Card
            key={item.id}
            className={selectedId === item.id ? 'selected' : ''}
            onClick={() => onItemClick?.(item)}
          >
            {/* UUID Row */}
            <CardRow>
              <Label>UUID:</Label>
              <UUIDValue>
                {item.id ? '...' + String(item.id).slice(-8) : 'N/A'}
              </UUIDValue>
            </CardRow>

            {/* Show title only if no form data or title is different from form fields */}
            {(!formData?.content || formData.content.length === 0) && (
              <CardRow>
                <Label>Tên:</Label>
                <Value>{item.title || 'Chưa có tên'}</Value>
              </CardRow>
            )}

            {item.username && (
              <CardRow>
                <Label>Username:</Label>
                <UsernameValue>@{item.username}</UsernameValue>
              </CardRow>
            )}

            {(item.email || item.phoneNumber) && (
              <CardRow>
                <Label>Liên hệ:</Label>
                <ContactContainer>
                  {item.email && <ContactItem>{item.email}</ContactItem>}
                  {item.phoneNumber && <ContactItem>{item.phoneNumber}</ContactItem>}
                </ContactContainer>
              </CardRow>
            )}

            {(item.rolesArray || item.isOwner) && (
              <CardRow>
                <Label>Vai trò:</Label>
                <RolesContainer>
                  {item.isOwner ? (
                    <Tag color="gold" icon={<CrownOutlined />}>
                      Chủ dự án
                    </Tag>
                  ) : (
                    item.rolesArray?.map((role: any, index: number) => (
                      <Tag color="blue" key={index}>
                        {getRoleDisplayName(role.name || role)}
                      </Tag>
                    ))
                  )}
                </RolesContainer>
              </CardRow>
            )}

            {item.tier && (
              <CardRow>
                <Label>Gói:</Label>
                <TierContainer>
                  <TierValue tier={item.tier}>{item.tier}</TierValue>
                  {item.expireDate && (
                    <ExpireDate>HSD: {item.expireDate}</ExpireDate>
                  )}
                </TierContainer>
              </CardRow>
            )}

            {/* Dynamic Fields from Form Content */}
            {formData?.content?.map((field) => {
              const value = item.properties?.[field.key] || item.data?.[field.key];
              if (!value && value !== 0) return null;

              let displayValue = String(value);
              if (field.type === 'date' && typeof value === 'number') {
                displayValue = formatDate(value);
              }

              return (
                <CardRow key={field.key}>
                  <Label>{field.label || field.key}:</Label>
                  <DynamicValue>{displayValue}</DynamicValue>
                </CardRow>
              );
            })}

            {item.description && (
              <CardRow>
                <Label>Mô tả:</Label>
                <Value>{item.description.length > 60 ? item.description.substring(0, 60) + '...' : item.description}</Value>
              </CardRow>
            )}

            {item.geom && (
              <CardRow>
                <Label>Hình học:</Label>
                <GeometryContainer>
                  {(() => {
                    try {
                      const geometry = JSON.parse(item.geom);
                      return (
                        <GeometryPreview
                          geometry={geometry}
                          width={40}
                          height={40}
                        />
                      );
                    } catch (error) {
                      return <span style={{ fontSize: '12px', color: '#ccc' }}>Invalid geometry</span>;
                    }
                  })()}
                </GeometryContainer>
              </CardRow>
            )}

            {/* Siêu dữ liệu Section */}
            <MetadataSection>
              <MetadataHeader onClick={(e) => toggleMetadata(item.id, e)}>
                <MetadataTitle style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <PiChartBar style={{ color: '#1677ff', fontSize: 16 }} />
                  <span>Siêu dữ liệu</span>
                </MetadataTitle>
                <ExpandButton>
                  {expandedMetadata.has(item.id) ? <UpOutlined /> : <DownOutlined />}
                </ExpandButton>
              </MetadataHeader>

              {expandedMetadata.has(item.id) && (
                <MetadataContent>
                  {(item.lat !== undefined && item.lng !== undefined) && (
                    <CardRow>
                      <Label>Tọa độ:</Label>
                      <CoordinateContainer>
                        <CoordinateText>
                          {item.lat.toFixed(6)}, {item.lng.toFixed(6)}
                        </CoordinateText>
                        <Tooltip title="Copy tọa độ">
                          <CopyButton
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(`${item.lat.toFixed(6)}, ${item.lng.toFixed(6)}`);
                            }}
                          />
                        </Tooltip>
                      </CoordinateContainer>
                    </CardRow>
                  )}

                  {item.formName && (
                    <CardRow>
                      <Label>Biểu mẫu:</Label>
                      <Value>{item.formName}</Value>
                    </CardRow>
                  )}

                  {item.creator && (
                    <CardRow>
                      <Label>Người tạo:</Label>
                      <CreatorValue
                        onClick={(e) => {
                          e.stopPropagation();
                          if (item.creatorId) {
                            onCreatorClick?.(item.creatorId);
                          }
                        }}
                      >
                        {item.creator}
                      </CreatorValue>
                    </CardRow>
                  )}

                  {item.createdAt && (
                    <CardRow>
                      <Label>Ngày tạo:</Label>
                      <Value>{formatDate(item.createdAt)}</Value>
                    </CardRow>
                  )}

                  {item.syncAt && (
                    <CardRow>
                      <Label>Ngày đồng bộ:</Label>
                      <Value>{formatDate(item.syncAt)}</Value>
                    </CardRow>
                  )}
                </MetadataContent>
              )}
            </MetadataSection>

            {/* Action Buttons */}
            {(onEditClick || onDeleteClick) && (
              <ActionRow>
                <Space size="middle">
                  {onEditClick && (
                    <Tooltip title="Sửa">
                      <ActionButton
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditClick(item);
                        }}
                      />
                    </Tooltip>
                  )}
                  {onDeleteClick && (
                    <Popover
                      content={
                        <div style={{ width: 180 }}>
                          <div style={{ marginBottom: 8 }}>Bạn có chắc chắn muốn xóa?</div>
                          <Space>
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmVisible(null);
                                onDeleteClick(item);
                              }}
                              danger
                            >
                              Xóa
                            </Button>
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmVisible(null);
                              }}
                            >
                              Hủy
                            </Button>
                          </Space>
                        </div>
                      }
                      title="Xác nhận xóa"
                      trigger="click"
                      open={deleteConfirmVisible === item.id}
                      onOpenChange={(visible) =>
                        setDeleteConfirmVisible(visible ? item.id : null)
                      }
                    >
                      <Tooltip title="Xóa">
                        <ActionButton
                          type="text"
                          size="small"
                          icon={<DeleteOutlined />}
                          danger
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Tooltip>
                    </Popover>
                  )}
                </Space>
              </ActionRow>
            )}
          </Card>
        ))}

      </CardList>

      {/* Enhanced Pagination */}
      {pagination && data.length > 0 && (
        <PaginationContainer>
          <PaginationRow>
            <PaginationControls>
              <PaginationButton
                disabled={pagination.current <= 1}
                onClick={() => pagination.onChange(1, pagination.pageSize)}
                title="Trang đầu"
              >
                ««
              </PaginationButton>

              <PaginationButton
                disabled={pagination.current <= 1}
                onClick={() => pagination.onChange(pagination.current - 1, pagination.pageSize)}
                title="Trang trước"
              >
                ‹
              </PaginationButton>

              <PageNumber>
                {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)}
              </PageNumber>

              <PaginationButton
                disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                onClick={() => pagination.onChange(pagination.current + 1, pagination.pageSize)}
                title="Trang sau"
              >
                ›
              </PaginationButton>

              <PaginationButton
                disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                onClick={() => pagination.onChange(Math.ceil(pagination.total / pagination.pageSize), pagination.pageSize)}
                title="Trang cuối"
              >
                »»
              </PaginationButton>
            </PaginationControls>

            <PageSizeSelector>
              <CustomDropdown>
                <DropdownButton
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  <span>{pagination.pageSize}</span>
                  <DownOutlined style={{ fontSize: '10px', marginLeft: '4px' }} />
                </DropdownButton>

                {isDropdownOpen && (
                  <>
                    <DropdownOverlay onClick={() => setIsDropdownOpen(false)} />
                    <DropdownMenu>
                      {[10, 15, 20, 30, 50].map(size => (
                        <DropdownItem
                          key={size}
                          selected={pagination.pageSize === size}
                          onClick={() => {
                            pagination.onChange(1, size);
                            setIsDropdownOpen(false);
                          }}
                        >
                          {size}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </>
                )}
              </CustomDropdown>
            </PageSizeSelector>
          </PaginationRow>
        </PaginationContainer>
      )}
    </Container>
  );
};

export default memo(MobileCardList);

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const CardList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  padding-bottom: 80px; /* Space for sticky pagination - adjust based on pagination height */
  gap: 12px;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Important for flex children with scroll */
`;

const Card = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #3b82f6;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
  }

  &.selected {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%);
    border-color: #3b82f6;
    border-width: 2px;
  }
`;

const CardRow = styled.div`
  display: flex;
  margin-bottom: 4px;
  align-items: flex-start;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  font-weight: 600;
  color: #64748b;
  font-size: 12px;
  min-width: 80px;
  margin-right: 8px;
  flex-shrink: 0;
`;

const Value = styled.span`
  font-size: 14px;
  color: #1e293b;
  flex: 1;
  word-wrap: break-word;
`;

const CreatorValue = styled(Value)`
  color: #3b82f6;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const UUIDValue = styled(Value)`
  font-family: monospace;
  font-size: 12px;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  color: #64748b;
`;

const DynamicValue = styled(Value)`
  font-weight: 500;
  color: #1e293b;
`;

const TypeValue = styled(Value)`
  font-family: monospace;
  font-size: 12px;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
`;

const LoadingContainer = styled.div`
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LoadingCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  height: 80px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  padding: 8px 12px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 8px 8px;
  flex-shrink: 0; /* Prevent pagination from shrinking */
  position: sticky;
  bottom: 0;
  z-index: 10;
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const PageSizeSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
`;

const CustomDropdown = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: white;
  color: #374151;
  cursor: pointer;
  min-width: 55px;
  height: 36px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &:hover {
    border-color: #9ca3af;
  }
`;

const DropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
`;

const DropdownMenu = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 -10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 80px;
  margin-bottom: 4px;
`;

const DropdownItem = styled.div<{ selected: boolean }>`
  padding: 12px 16px;
  font-size: 16px;
  color: #374151;
  cursor: pointer;
  background: ${props => props.selected ? '#f3f4f6' : 'white'};
  font-weight: ${props => props.selected ? '600' : '400'};

  &:hover {
    background: #f9fafb;
  }

  &:first-child {
    border-radius: 6px 6px 0 0;
  }

  &:last-child {
    border-radius: 0 0 6px 6px;
  }

  &:only-child {
    border-radius: 6px;
  }
`;


const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: white;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.05);
    color: #3b82f6;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PageNumber = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #1e293b;
  padding: 0 8px;
`;

const GeometryContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;


const UsernameValue = styled(Value)`
  color: #6b7280;
  font-family: monospace;
  font-size: 13px;
`;

const RoleValue = styled(Value)`
  color: #3b82f6;
  font-weight: 500;
`;

const RolesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ContactItem = styled.span`
  font-size: 12px;
  color: #4b5563;
  background: #f3f4f6;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
`;

const TierContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TierValue = styled.span<{ tier: string }>`
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
  width: fit-content;
  color: white;
  background: ${props => {
    switch (props.tier?.toUpperCase()) {
      case 'PREMIUM': return '#f59e0b';
      case 'PRO': return '#8b5cf6';
      case 'ENTERPRISE': return '#ef4444';
      default: return '#6b7280';
    }
  }};
`;

const ExpireDate = styled.span`
  font-size: 10px;
  color: #64748b;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;

  &:hover {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.05);
  }

  &.ant-btn-dangerous:hover {
    border-color: #ef4444;
    background: rgba(239, 68, 68, 0.05);
  }
`;

const MetadataSection = styled.div`
  margin-top: 12px;
  padding: 8px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 6px;
`;

const MetadataHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 4px 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 8px;

  &:hover {
    background: rgba(59, 130, 246, 0.05);
    border-radius: 4px;
    margin: 0 -4px 8px -4px;
    padding: 4px;
  }
`;

const MetadataTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const ExpandButton = styled.div`
  color: #6b7280;
  font-size: 12px;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;

  &:hover {
    color: #3b82f6;
  }
`;

const MetadataContent = styled.div`
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
      overflow: hidden;
    }
    to {
      opacity: 1;
      max-height: 500px;
      overflow: visible;
    }
  }
`;

const CoordinateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CoordinateText = styled.span`
  font-family: monospace;
  font-size: 12px;
  background: #ffffff;
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  color: #1e293b;
  flex: 1;
`;

const CopyButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #ffffff;

  &:hover {
    border-color: #3b82f6;
    background: rgba(59, 130, 246, 0.05);
    color: #3b82f6;
  }
`;

